import { KnownClearingHouses } from './BillingCode.type';

/**
 * Integration connector contract — the vendor-agnostic seam for API-first
 * clearinghouses (docs/connection-api/INFRASTRUCTURE_PLAN.md §3a).
 *
 * Vendor implementations live ONLY under
 * `libs/server-core/.../integration.helpers/connector/vendors/<vendor>/` and
 * are registered as ONE public factory per vendor. Generic code never
 * references a vendor by name (pinning test enforces this).
 */

export enum ConnectorScope {
  /** One ChiroUp account at the vendor; credentials/polling are platform-wide. */
  Platform = 'platform',
  /** Credentials live on each clinic's Integration row (no current vendor). */
  Clinic = 'clinic',
}

export enum IntegrationDocumentType {
  Claim837P = '837P',
  Remit835 = '835',
  Ack277CA = '277CA',
  Ack999 = '999',
  Req270 = '270',
  Res271 = '271',
}

export type ConnectorCapabilities = {
  submit: IntegrationDocumentType[];
  poll: boolean;
  webhook: boolean;
};

/**
 * Pointer to one inbound document discovered by poll or webhook. This is the
 * ONLY thing a webhook is trusted to deliver (doorbell, not delivery — plan
 * §3d); document content always comes from `fetchDocument`.
 */
export type InboundDocumentRef = {
  /** Vendor's transaction id (account-unique; layer-1 dedupe key, plan §4.1). */
  partnerTransactionId: string;
  docType: IntegrationDocumentType;
  /**
   * When the vendor created/received the transaction — epoch ms (house time
   * format). Each vendor FACTORY maps its wire format here (Stedi: ISO
   * `createdAt` → `Date.parse`); vendor formats never cross this seam.
   */
  receivedAt?: number;
};

/**
 * ONE page of a vendor poll (plan §6 INFRA-6). `poll` fetches a single page
 * per call — the drain owns the page loop, so it can persist the cursor and
 * enforce its page cap between calls (a connector-internal loop could
 * silently swallow both).
 */
export type ConnectorPollResult = {
  refs: InboundDocumentRef[];
  /**
   * The vendor's resume cursor: pass it back as `pageToken` to continue.
   * Persisted on the platform row as the PRIMARY watermark (plan §7 item 2) —
   * absence means the vendor did not advance the cursor, never "start over".
   */
  nextPageToken?: string;
};

/**
 * The persisted poll watermark — `specifics.pollCursor` on the vendor's
 * clinicId = 0 platform Integration row, written ONLY via
 * `setPlatformSpecificsKey` (plan §6 INFRA-6, §7 item 2). The drain advances
 * it only after a page's refs have all been ingested; a run that throws
 * leaves it untouched, so the next run re-reads what the failed run saw and
 * the dedupe gate swallows the overlap.
 */
export type ConnectorPollCursor = {
  /** Vendor's `nextPageToken` — the primary watermark. */
  pageToken?: string;
  /**
   * Recovery floor (ms): the newest instant known fully covered. When the
   * pageToken is missing or the vendor rejects it, polling restarts from
   * this minus the vendor's `polling.recoveryOverlapMs` and dedupe layer 1
   * makes the re-read harmless. Advances to the newest `receivedAt`
   * ingested; an empty (caught-up) page advances it to the poll time minus
   * the vendor's `polling.recencyExclusionMs`.
   */
  watermarkMs: number;
};

/**
 * Persisted per-vendor drain timing (`specifics.pollStats`, INFRA-6 —
 * Brian, 2026-07-21): an EWMA of per-document ingest wall-clock, carried
 * across runs so the drain's time-budget check ("can the NEXT document
 * finish before the Lambda deadline?") estimates from observed history
 * instead of a guess. Written only via `setPlatformSpecificsKey`.
 */
export type ConnectorPollStats = {
  /** EWMA of one document's ingest duration (wall-clock ms). */
  avgDocMs: number;
  /** Documents folded into the average, lifetime. */
  docs: number;
  updatedAt: number;
};

/**
 * Outbound request auth, per scheme not per vendor (plan §3b). `api-key` and
 * `basic` are implemented; `oauth2-client-credentials` is interface-only until
 * a vendor requires it. The authenticator that APPLIES these lives in
 * server-core's connector layer (it is axios-coupled).
 */
export type IntegrationApiAuth =
  | { scheme: 'api-key'; apiKey: string; header?: string; prefix?: string }
  | { scheme: 'basic'; username: string; password: string }
  | {
      scheme: 'oauth2-client-credentials';
      tokenUrl: string;
      clientId: string;
      clientSecret: string;
      scopes?: string[];
    }
  | { scheme: 'custom'; kind: string; params: Record<string, string> };

/**
 * Credentials resolved per the connector's scope (plan §3b): platform scope
 * reads the vendor's Secrets Manager secret; clinic scope reads the clinic's
 * `Integration.specifics`. Connectors receive these per call — they hold no
 * state of their own (Lambda warm-start rule).
 */
export type ConnectorCredentials = {
  auth: IntegrationApiAuth;
  /** Overrides the vendor's default API base URL (test vs production hosts). */
  baseUrl?: string;
};

/** Result of submitting ONE claim (one exchange row = one claim, plan §3c). */
export type ClaimSubmissionResult = {
  status: 'submitted' | 'rejected';
  /**
   * Vendor's id for the accepted claim — stored in
   * `IntegrationExchange.partnerTransactionId` (the 277CA correlation key,
   * plan §3c.2; the invoice reaches it via `Invoice.exchangeId`).
   */
  partnerTransactionId?: string;
  /** Vendor edit/validation rejections, verbatim. Never logged with PHI. */
  errors?: unknown[];
  /** Vendor's full response body for `meta` bookkeeping. */
  raw?: unknown;
};

/**
 * One fetched inbound document: the vendor's COMPLETE report response,
 * verbatim (`raw` — persisted through the payload envelope, never trimmed; plan §3d),
 * plus the canonical structure the existing handlers consume (`payload` — e.g.
 * a pre-parsed advice for `handle835Response`).
 */
export type CanonicalInboundDocument = {
  docType: IntegrationDocumentType;
  payload: unknown;
  raw: unknown;
};

export interface IntegrationConnector {
  vendor: KnownClearingHouses;
  scope: ConnectorScope;
  capabilities: ConnectorCapabilities;

  /**
   * Vendor-documented polling characteristics — facts about the VENDOR's
   * feed, not drain policy, so they live in the factory and every new vendor
   * must state its own (required on purpose: silently inheriting another
   * vendor's numbers is the bug).
   */
  polling: {
    /**
     * Feed recency exclusion: same-instant transactions can surface this
     * late, so an empty page proves coverage only to poll-time minus this
     * window — the drain's watermark never advances into it.
     */
    recencyExclusionMs: number;
    /**
     * Overlap below the watermark floor on token-less recovery — how far
     * before the last processed transaction the vendor recommends
     * restarting; dedupe layer 1 makes the re-read a silent skip.
     */
    recoveryOverlapMs: number;
  };

  /**
   * The vendor-specific renderer (plan §3c.1, journal Resolved 21): takes
   * createBatchSet's IN-MEMORY X12 output — the artifact every CBS decision
   * path produced — and returns the vendor's SUBMISSION DOCUMENT (Stedi: the
   * literal professional-claims request body). Implemented as vendor-neutral
   * `parse837` + a table-driven vendor codec: a transcription of what CBS
   * emitted, never a re-derivation, so CBS stays the single source of truth.
   * The x12 input is transient and NEVER stored; the returned document is
   * persisted verbatim through the payload envelope and transmitted byte-identically.
   * Pure and synchronous — render failures surface at build time, inside the
   * build transaction. Throws on documents it cannot render.
   */
  renderClaim(args: { x12: string }): unknown;

  submitClaim(args: {
    credentials: ConnectorCredentials;
    /** `renderClaim`'s output, read back through the payload envelope — transmitted verbatim. */
    document: unknown;
    /**
     * TRANSMISSION identity, not claim or encounter identity (plan §3c.3):
     * always `exchangeId`, so transport retries of one delivery (SQS
     * redelivery, worker crash, backstop re-enqueue) collapse into ONE claim
     * at the vendor, while every new submission — corrected resubmission,
     * secondary after primary — is a new exchange row and therefore a fresh
     * key that the vendor's idempotency window must NOT swallow. NEVER key
     * this on `billingKey`: encounter scope would silently eat corrections
     * and collide primary/secondary claims inside the vendor's 24 h window.
     */
    idempotencyKey: string;
  }): Promise<ClaimSubmissionResult>; // one exchange row = one claim (§3c)

  /**
   * ONE page of the vendor's account feed (plan §6 INFRA-6). `pageToken`
   * (the persisted watermark) wins when present; `sinceMs` is the
   * floor-recovery/first-run start when there is no token — epoch ms, the
   * house time format; each connector converts to whatever its vendor's API
   * wants (Stedi: ISO-8601 `startDateTime`). The drain owns the page loop,
   * cursor persistence, and the page cap.
   */
  poll(args: {
    credentials: ConnectorCredentials;
    sinceMs: number; // start when no pageToken (platform-level for platform-scoped vendors)
    pageToken?: string;
  }): Promise<ConnectorPollResult>;

  fetchDocument(args: {
    credentials: ConnectorCredentials;
    ref: InboundDocumentRef;
  }): Promise<CanonicalInboundDocument>;

  /**
   * The PARSE half of `fetchDocument`, standalone: vendor report JSON → the
   * canonical structure the existing handlers consume. PURE — no vendor
   * call — so the retry path can rebuild a document from the STORED payload
   * (the raw report persisted at ingest) without re-fetching bytes that
   * must stay identical to what was hashed. `fetchDocument` composes fetch +
   * this. Throws on doc types the vendor cannot parse.
   */
  parseDocument(args: { docType: IntegrationDocumentType; raw: unknown }): unknown;

  /**
   * Layer-3 content identity for ONE fetched document (plan §4.3) — each
   * vendor picks its own canonicalization rather than a one-size-fits-all
   * hash. Return a LABELED hash (`<label>:<hex>`, e.g. `v1:<md5>`) or null
   * to opt the doc type out of content dedupe (layers 1–2 still apply;
   * remits then park on ANY TRN recurrence — the conservative disposition).
   * The label partitions hash spaces — two canonicalizations, or a
   * canonicalization and the legacy X12 guard's bare md5s sharing the
   * `checksum_fuzzy` column, can never cross-match — and the layer-3 lookup
   * REFUSES unlabeled values. Pure and synchronous.
   */
  calculateChecksumFuzzy(args: { document: CanonicalInboundDocument }): string | null;
}
